import { getStoredUserNotificationPrefs } from "../containers/Settings/NotificationCenter";

/**
 * Checks if a notification type is allowed based on the current user's preferences.
 *
 * Reads the FCM token from localStorage and fetches the user's notification
 * preferences (via Redux store or API fallback in `getStoredUserNotificationPrefs`).
 * The preference object is keyed by FCM token and has the shape:
 * ```js
 * {
 *   "<fcm_token>": {
 *     global_enabled: boolean,
 *     account_notification_enabled: boolean,
 *     transaction_notification_enabled: boolean
 *   }
 * }
 * ```
 *
 * Decision logic:
 * 1. If `global_enabled` is false → block all notifications
 * 2. If type is "account" → return `account_notification_enabled`
 * 3. If type is "transaction" → return `transaction_notification_enabled`
 * 4. Unknown type → fall back to `global_enabled`
 *
 * Used by both the main app (App.js foreground handler) and the service worker
 * (firebase-messaging-sw.js background handler) to filter notifications before display.
 *
 */

const isNotificationTypeAllowed = async (notificationType) => {
  const token = localStorage.getItem("fcm_token");
  const prefs = await getStoredUserNotificationPrefs();
  const globalEnabled = prefs?.[token]?.global_enabled;
  const accountEnabled = prefs?.[token]?.account_notification_enabled;
  const transactionEnabled = prefs?.[token]?.transaction_notification_enabled;

  // If global is disabled, block all notifications
  if (!globalEnabled) {
    return false;
  }

  // For accounts notification
  if (notificationType === "account") {
    return accountEnabled;
  }

  // For transaction notification
  if (notificationType === "transaction") {
    return transactionEnabled;
  }

  // Default to global if type is unknown
  return globalEnabled;
};

export { isNotificationTypeAllowed };
